import React from 'react';
import LegalLayout from '../components/LegalLayout';

const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" lastUpdated="August 8, 2026">
    <p>
      This Privacy Policy describes how Create by Lumera ("we", "us", "our"), operating under the domain <strong>createbylumera.com</strong>, collects, uses, shares, and protects your personal information when you use Create by Lumera and related services (the "Service").
    </p>
    <p>
      By using our Service, you agree to the collection and use of information in accordance with this policy.
    </p>

    <h2>1. Information We Collect</h2>

    <h3>1.1 Account Information</h3>
    <p>When you create an account, we collect:</p>
    <ul>
      <li>Full name</li>
      <li>Email address</li>
      <li>Password (stored securely using industry-standard hashing)</li>
      <li>Profile picture (optional)</li>
    </ul>

    <h3>1.2 Payment Information</h3>
    <p>When you subscribe to our Pro plan, payment is processed by our third-party payment providers:</p>
    <ul>
      <li><strong>Stripe</strong> — processes credit/debit card payments and manages recurring subscriptions. We store your Stripe customer ID and subscription ID, but never your full card number.</li>
      <li><strong>PayPal</strong> — processes one-time payments. We store your PayPal order ID, but not your PayPal account credentials.</li>
    </ul>
    <p>Payment providers handle card data directly under their own privacy policies and PCI-DSS compliance.</p>

    <h3>1.3 Generated Content</h3>
    <p>We store the digital products you generate (planners, journals, guides, etc.), including:</p>
    <ul>
      <li>Product titles, descriptions, and configuration settings</li>
      <li>Generated images and text content</li>
      <li>Collections and saved favorites</li>
    </ul>

    <h3>1.4 Third-Party Integration Data</h3>
    <p>When you connect external services, we store:</p>
    <ul>
      <li><strong>Pinterest</strong> — access token, board data, publish history (via Pinterest API v5)</li>
      <li><strong>Shopify</strong> — store domain, Admin API access token, export history</li>
    </ul>
    <p>These tokens are stored securely and used solely to perform actions you explicitly request.</p>

    <h3>1.5 Automatically Collected Data</h3>
    <ul>
      <li>Browser type, operating system, and device information</li>
      <li>IP address and approximate geographic location</li>
      <li>Pages visited, features used, and timestamps</li>
      <li>Cookies and similar technologies (see our <a href="/cookies">Cookie Policy</a>)</li>
    </ul>

    <h2>2. How We Use Your Information</h2>
    <ul>
      <li>To provide, maintain, and improve the Service</li>
      <li>To process payments and manage subscriptions</li>
      <li>To generate and store digital products on your behalf</li>
      <li>To connect and interact with third-party services (Pinterest, Shopify) at your request</li>
      <li>To send transactional emails (account confirmation, payment receipts, Pro welcome emails)</li>
      <li>To detect and prevent fraud or abuse</li>
      <li>To comply with legal obligations</li>
    </ul>

    <h2>3. AI Content Generation</h2>
    <p>
      Our Service uses OpenAI's language models to generate content. Text prompts you provide may be sent to OpenAI's API for processing. We do not use your generated content to train AI models. Please review <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">OpenAI's Privacy Policy</a> for their data handling practices.
    </p>

    <h2>4. Data Sharing</h2>
    <p>We do not sell your personal data. We share information only in the following cases:</p>
    <ul>
      <li><strong>Payment Processors</strong> — Stripe and PayPal receive necessary payment information to process transactions.</li>
      <li><strong>Third-Party Integrations</strong> — Pinterest and Shopify receive data only when you explicitly connect and use these features.</li>
      <li><strong>Email Service</strong> — We use Resend to deliver transactional emails. Only your email address is shared.</li>
      <li><strong>AI Providers</strong> — OpenAI processes content generation requests. No personal account data is sent.</li>
      <li><strong>Legal Requirements</strong> — We may disclose information if required by law or to protect our rights.</li>
    </ul>

    <h2>5. Data Retention</h2>
    <p>
      We retain your account data and generated content for as long as your account is active. If you delete your account, we will delete your personal data within 30 days, except where retention is required by law (e.g., payment records for tax compliance).
    </p>

    <h2>6. Data Security</h2>
    <p>
      We use industry-standard security measures including encrypted connections (TLS/SSL), hashed passwords (bcrypt), and secure token storage. However, no system is 100% secure. We recommend using a strong, unique password for your account.
    </p>

    <h2>7. Your Rights</h2>
    <p>Depending on your jurisdiction, you may have the right to:</p>
    <ul>
      <li>Access the personal data we hold about you</li>
      <li>Correct inaccurate data</li>
      <li>Delete your account and associated data</li>
      <li>Export your data in a portable format</li>
      <li>Withdraw consent for data processing</li>
      <li>Object to certain types of processing</li>
    </ul>
    <p>To exercise these rights, contact us at <strong>support@createbylumera.com</strong>.</p>

    <h2>8. International Data Transfers</h2>
    <p>
      Our Service is hosted on cloud infrastructure that may process data in different jurisdictions. By using the Service, you consent to the transfer of your information to countries that may have different data protection laws than your country of residence.
    </p>

    <h2>9. Children's Privacy</h2>
    <p>
      Our Service is not intended for users under the age of 16. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal data, we will delete it promptly.
    </p>

    <h2>10. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by posting a notice on the Service. Continued use of the Service after changes constitutes acceptance.
    </p>

    <h2>11. Contact</h2>
    <p>If you have questions about this Privacy Policy, contact us:</p>
    <ul>
      <li>Email: <strong>support@createbylumera.com</strong></li>
      <li>Website: <a href="https://createbylumera.com">createbylumera.com</a></li>
    </ul>
  </LegalLayout>
);

export default PrivacyPolicy;
